/** Add: a figure recorded by hand, in a side drawer - ADR-0212 decision 10.
 *
 * **It creates the same record the extraction path creates** (`POST /inventory/activities/manual`,
 * ADR-0036): an activity record on a person's attestation, graded by the server and never by the
 * caller, which arrives unverified and counts in the totals as a stated basis until a different
 * Admin or Approver accepts it (ADR-0067). So the table never holds two kinds of row, and the
 * Source column says *Manual*.
 *
 * What a table may be added to is the table's: Scope 1 offers fuel burned and refrigerant, Scope 2
 * electricity, and Scope 3 only what the engine can price (transmission loss). Each is offered **only
 * where the site's factor set publishes a factor for it** - the factor library is the authority on
 * what is supported (F06), so there is no list of fuels here, and nothing is substituted. The
 * category and the fuel are never defaulted: a figure filed under a guessed fuel is a wrong figure
 * with a complete receipt.
 *
 * The form is a drawer and not a page for the reason the other short writes are: it is one act that
 * the person returns from to the table they came from. Its explanations are `scope1.add-*` info.
 */

import { useState } from "react";

import { useFacilities } from "../../api/evidence";
import { useFactorLibrary } from "../../api/factors";
import { useReaderNow } from "../../api/profile";
import { useRecordManualEntry } from "../../api/queries";
import type { ManualEntryResult } from "../../api/queries";
import { isPeriodClosed } from "../../api/refusal";
import { FieldError } from "../../components/FieldError";
import { ReadFailed, WriteFailed } from "../../components/Loaded";
import { Drawer, InfoPopover } from "../../components/ui";
import { useFormChecks } from "../../components/useFormChecks";
import { readDecimal } from "../../lib/decimalInput";
import { carriersIn, setFor } from "../../lib/manualCarriers";
import { ManualMeterPicker } from "../MeterPicker";
import { PostCloseRetry } from "../PeriodLock";
import { words } from "./model";
import type { Scope } from "./model";
import styles from "./scopeTable.module.css";

/** What each table may add, with the words a person knows it by. A category is offered only if the
 * site's factor set has a factor for it. */
const ADDABLE: Readonly<Record<Scope, ReadonlyArray<{ value: string; label: string }>>> = {
  "1": [
    { value: "stationary", label: "Fuel burned on site" },
    { value: "mobile", label: "Fuel burned in vehicles" },
    { value: "fugitive", label: "Refrigerant lost" },
  ],
  "2": [{ value: "electricity", label: "Electricity purchased" }],
  "3": [{ value: "transmission_loss", label: "Transmission and distribution loss" }],
};

/** How the figure was reached, with the grade shown **before** the choice. The grade is recorded by
 * the server from the method (`ESTIMATE_QUALITY`); these letters are the same table, for reading. */
const BASIS_KIND: ReadonlyArray<{ value: string; label: string; grade: string }> = [
  { value: "", label: "Read or measured", grade: "B" },
  { value: "pro_rata", label: "Split from a larger figure", grade: "B" },
  { value: "prior_year_proxy", label: "Last year's figure again", grade: "C" },
  { value: "degree_day", label: "Modelled from the weather", grade: "C" },
];

/** The shortest basis the server accepts (`stated_basis`), said on the screen so the sentence beside
 * the box can count against it. */
const MIN_BASIS = 12;

/** The API's field names for this form's, so a refusal lands beside its own box. */
const SERVER_FIELD: Readonly<Record<string, string>> = {
  facility_id: "site",
  category: "category",
  fuel_or_carrier: "carrier",
  quantity: "amount",
  unit: "unit",
  period_start: "from",
  period_end: "to",
  stated_basis: "basis",
};

/** What to say beside an Amount that is empty or cannot be read as a number. */
function amountSentence(typed: string, locale: string | undefined): string {
  const read = readDecimal(typed, locale);
  if (!read.ok) return read.sentence;
  return "Type how much, as a number such as 4200 or 4.2.";
}

export function AddFigureDrawer({
  scope,
  open,
  onClose,
}: {
  scope: Scope;
  open: boolean;
  onClose: () => void;
}) {
  const facilities = useFacilities(open);
  const library = useFactorLibrary(open);
  const record = useRecordManualEntry();

  const [facilityId, setFacilityId] = useState("");
  const [category, setCategory] = useState("");
  const [carrier, setCarrier] = useState("");
  const [meterId, setMeterId] = useState("");
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState(scope === "2" || scope === "3" ? "kWh" : "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [estimate, setEstimate] = useState("");
  const [basis, setBasis] = useState("");
  const [done, setDone] = useState<ManualEntryResult | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);

  const site = facilities.data?.find((candidate) => candidate.id === facilityId);
  const factorSet = site && library.data ? setFor(library.data, site.jurisdiction) : undefined;
  const categoryOptions = factorSet
    ? ADDABLE[scope].filter((option) => factorSet.factors.some((f) => f.category === option.value))
    : [];
  const carrierOptions = factorSet && category ? carriersIn(factorSet, category) : [];
  const readerNow = useReaderNow();
  const [checks, attach] = useFormChecks();

  /** The rules, in the order the fields are on the screen, so the first failure is the first box a
   * person meets and focus goes there (Q-06). The button is never disabled for these: pressing it is
   * how a person learns what is missing (G-Wcontrib-10). */
  function rules(quantity: string) {
    return [
      { field: "site", ok: facilityId !== "", message: "Choose the site this figure is for." },
      {
        field: "category",
        ok: category !== "",
        message: facilityId
          ? "Choose the kind of activity."
          : "Choose a site first, then the kind of activity.",
      },
      {
        field: "carrier",
        ok: carrierOptions.includes(carrier),
        message: category ? "Choose the fuel or carrier." : "Choose a kind of activity first.",
      },
      {
        field: "amount",
        ok: quantity !== "",
        message: amountSentence(amount, readerNow()?.locale),
      },
      { field: "unit", ok: unit.trim() !== "", message: "Say the unit, such as kWh or litres." },
      { field: "from", ok: from !== "", message: "Choose the day the period starts." },
      { field: "to", ok: to !== "", message: "Choose the day the period ends." },
      {
        field: "to",
        ok: from === "" || to === "" || to >= from,
        message: "The period cannot end before it starts.",
      },
      {
        field: "basis",
        ok: basis.trim().length >= MIN_BASIS,
        message: `Say what the figure is based on, in at least ${MIN_BASIS} characters (${basis.trim().length} so far).`,
      },
    ];
  }

  function reset() {
    setFacilityId("");
    setCategory("");
    setCarrier("");
    setMeterId("");
    setAmount("");
    setUnit(scope === "2" || scope === "3" ? "kWh" : "");
    setFrom("");
    setTo("");
    setEstimate("");
    setBasis("");
    setDone(null);
    setRefusal(null);
    checks.clear();
  }

  function close() {
    reset();
    onClose();
  }

  async function submit(postCloseReason?: string) {
    setRefusal(null);
    setDone(null);
    // **The quantity is read as a string, never a number** (rule 5): "4,200" becomes "4200", and
    // anything that is not plainly a number is a sentence beside the box, with nothing sent.
    const quantity = readDecimal(amount, readerNow()?.locale);
    if (!checks.run(rules(quantity.ok ? quantity.value : ""))) return;
    if (!quantity.ok) return;
    try {
      const result = await record.mutateAsync({
        ...(postCloseReason ? { post_close_reason: postCloseReason } : {}),
        facility_id: facilityId,
        category,
        fuel_or_carrier: carrier,
        quantity: quantity.value,
        unit,
        period_start: from,
        period_end: to,
        stated_basis: basis,
        estimate_method: estimate || null,
        ...(meterId ? { meter_id: meterId } : {}),
      });
      setDone(result);
      setAmount("");
      setBasis("");
    } catch (error) {
      // A field the server named is said beside that field; anything else is the general failure.
      if (!checks.refuse(error, SERVER_FIELD)) setRefusal(error);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={close}
      title={`Add a Scope ${scope} figure`}
      width="md"
      testId="add-figure-drawer"
      footer={
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primary}
            disabled={record.isPending}
            data-testid="record-manual"
            onClick={() => void submit()}
          >
            {record.isPending ? "Recording…" : "Record it"}
          </button>
          <button type="button" className={styles.ghost} onClick={close} data-testid="manual-cancel">
            {done ? "Close" : "Cancel"}
          </button>
          <InfoPopover infoKey="scope1.add-manual" />
        </div>
      }
    >
      <form
        ref={attach}
        className={styles.form}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!record.isPending) void submit();
        }}
      >
        {facilities.error ? (
          <ReadFailed
            of="The list of sites"
            error={facilities.error}
            onRetry={() => void facilities.refetch()}
          />
        ) : (
          // **No picker when the list could not be read** (EXP-29): an empty dropdown reads as a
          // workspace with no sites, on the one form where choosing a site is the required act.
          <label className={styles.field}>
            Site
            <select
              aria-label="Which site?"
              {...checks.field("site")}
              value={facilityId}
              onChange={(event) => {
                checks.touch("site");
                setFacilityId(event.target.value);
                setCategory("");
                setCarrier("");
                setMeterId("");
              }}
            >
              <option value="">Choose a site</option>
              {(facilities.data ?? []).map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </select>
            <FieldError {...checks.say("site")} />
          </label>
        )}

        <label className={styles.field}>
          Kind of activity
          <select
            aria-label="What kind of activity?"
            {...checks.field("category")}
            value={category}
            disabled={categoryOptions.length === 0}
            onChange={(event) => {
              checks.touch("category");
              setCategory(event.target.value);
              setCarrier("");
              setMeterId("");
            }}
          >
            <option value="">{facilityId ? "Choose one" : "Choose a site first"}</option>
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <FieldError {...checks.say("category")} />
        </label>

        <label className={styles.field}>
          {scope === "2" ? "Carrier" : "Fuel or carrier"}
          <select
            aria-label="Which fuel or carrier?"
            {...checks.field("carrier")}
            value={carrier}
            disabled={carrierOptions.length === 0}
            onChange={(event) => {
              checks.touch("carrier");
              setCarrier(event.target.value);
              setMeterId("");
            }}
          >
            <option value="">{category ? "Choose one" : "Choose a kind first"}</option>
            {carrierOptions.map((option) => (
              <option key={option} value={option}>
                {words(option)}
              </option>
            ))}
          </select>
          <FieldError {...checks.say("carrier")} />
        </label>

        <ManualMeterPicker
          facilityId={facilityId}
          category={category}
          carrier={carrier}
          value={meterId}
          onChange={setMeterId}
        />

        <div className={styles.pair}>
          <label className={styles.field}>
            Amount
            <input
              aria-label="How much?"
              {...checks.field("amount")}
              value={amount}
              inputMode="decimal"
              onChange={(event) => {
                checks.touch("amount");
                setAmount(event.target.value);
              }}
            />
            <FieldError {...checks.say("amount")} />
          </label>
          <label className={styles.field}>
            Unit
            <input
              aria-label="In what unit?"
              {...checks.field("unit")}
              value={unit}
              onChange={(event) => {
                checks.touch("unit");
                setUnit(event.target.value);
              }}
            />
            <FieldError {...checks.say("unit")} />
          </label>
        </div>

        <div className={styles.pair}>
          <label className={styles.field}>
            From
            <input
              aria-label="Period start"
              type="date"
              {...checks.field("from")}
              value={from}
              onChange={(event) => {
                checks.touch("from");
                setFrom(event.target.value);
              }}
            />
            <FieldError {...checks.say("from")} />
          </label>
          <label className={styles.field}>
            To
            <input
              aria-label="Period end"
              type="date"
              {...checks.field("to")}
              value={to}
              onChange={(event) => {
                checks.touch("to");
                setTo(event.target.value);
              }}
            />
            <FieldError {...checks.say("to")} />
          </label>
        </div>

        <label className={styles.field}>
          How do you know?
          <select
            aria-label="How do you know?"
            value={estimate}
            onChange={(event) => setEstimate(event.target.value)}
          >
            {BASIS_KIND.map((kind) => (
              <option key={kind.value || "read"} value={kind.value}>
                {kind.label} (grade {kind.grade})
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          What is it based on?
          <textarea
            aria-label="What is it based on?"
            {...checks.field("basis")}
            value={basis}
            rows={3}
            onChange={(event) => {
              checks.touch("basis");
              setBasis(event.target.value);
            }}
          />
          <FieldError {...checks.say("basis")} />
        </label>

        {done ? (
          <p
            className={styles.done}
            role="status"
            data-testid={done.already_recorded ? "manual-already-recorded" : "manual-recorded"}
          >
            <strong>{done.already_recorded ? "Already recorded." : "Recorded."}</strong> {done.next_step}
          </p>
        ) : null}
        <PostCloseRetry error={refusal} pending={record.isPending} onRecord={(reason) => void submit(reason)} />
        <WriteFailed error={isPeriodClosed(refusal) ? null : refusal} of="the figure" />
      </form>
    </Drawer>
  );
}
